// Polymarket market input parser.
//
// Domain-first ingestion: any URL on polymarket.com that has at least one
// non-empty path segment is accepted. We do NOT gate on category prefixes
// (/event, /market, /markets, /events, /sports/nhl, /crypto, /politics,
// /weather, /economy, /culture, ...). Polymarket keeps adding new category
// routes; a path-prefix allowlist breaks every time they do.
//
// Used product-wide. Do not duplicate this validator anywhere else.

import type { Platform } from "./predictionTypes";

export type ParseReason =
  | "ok_market"            // valid market link, ready to ingest
  | "ok_question"          // free-text question (no URL)
  | "empty"                // empty input
  | "unsupported_domain"   // URL on a domain we don't support
  | "no_market_path"       // supported domain but pathname is empty (root)
  | "kalshi_unavailable"   // Phase 1: Kalshi temporarily disabled while we rebuild signal/routing
  | "invalid_url";         // looked like a URL but failed to parse

export interface ParsedInput {
  type: "url" | "question";
  platform: Platform;
  /** Normalized input (URL with https:// + tracking stripped, or trimmed text) */
  raw: string;
  /** UI-facing hint message */
  hint: string | null;
  /** True iff this is a Polymarket/Kalshi URL with a non-empty path */
  isValidMarket: boolean;
  /** Last non-empty path segment, used as market slug */
  marketSlug: string | null;
  /** Same as marketSlug; preserved for back-compat with existing call sites */
  marketId: string | null;
  /** Machine-readable reason — drives UI messaging */
  reason: ParseReason;
}

const POLY_HOST_RE = /(^|\.)polymarket\.com$/i;
const KALSHI_HOST_RE = /(^|\.)kalshi\.com$/i;

const TRACKING_PARAMS = new Set([
  "utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content",
  "gclid", "fbclid", "mc_cid", "mc_eid", "ref", "ref_src",
]);

const URL_HINT_RE = /^(https?:\/\/|www\.|[a-z0-9-]+\.[a-z]{2,})/i;

/**
 * Normalize a pasted string into a URL string when it looks like one.
 * - trims whitespace
 * - prepends https:// if missing
 * - leaves non-URL input untouched
 */
export function normalizeInput(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return "";
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  // Looks like a bare host (polymarket.com/...) → assume https
  if (URL_HINT_RE.test(trimmed) && trimmed.includes(".")) {
    return `https://${trimmed.replace(/^\/+/, "")}`;
  }
  return trimmed;
}

function stripTracking(url: URL): URL {
  const out = new URL(url.toString());
  const drop: string[] = [];
  out.searchParams.forEach((_v, k) => {
    if (TRACKING_PARAMS.has(k.toLowerCase())) drop.push(k);
  });
  drop.forEach((k) => out.searchParams.delete(k));
  return out;
}

function platformForHost(host: string): Platform | null {
  const h = host.toLowerCase();
  if (POLY_HOST_RE.test(h)) return "polymarket";
  if (KALSHI_HOST_RE.test(h)) return "kalshi";
  return null;
}

function looksLikeUrl(s: string): boolean {
  return /^https?:\/\//i.test(s) || /^[a-z0-9-]+(\.[a-z0-9-]+)+(\/|$)/i.test(s);
}

export function parseMarketInput(raw: string): ParsedInput {
  const normalized = normalizeInput(raw);

  if (!normalized) {
    return {
      type: "question", platform: "other", raw: "", hint: null,
      isValidMarket: false, marketSlug: null, marketId: null, reason: "empty",
    };
  }

  // Try as URL first if it smells like one
  if (looksLikeUrl(normalized)) {
    let url: URL;
    try {
      url = new URL(normalized);
    } catch {
      return {
        type: "url", platform: "other", raw: normalized,
        hint: "That doesn't look like a valid URL.",
        isValidMarket: false, marketSlug: null, marketId: null, reason: "invalid_url",
      };
    }

    const platform = platformForHost(url.hostname);
    if (!platform) {
      return {
        type: "url", platform: "other", raw: url.toString(),
        hint: "Unsupported domain — paste a Polymarket link.",
        isValidMarket: false, marketSlug: null, marketId: null, reason: "unsupported_domain",
      };
    }

    // Phase 1: Kalshi is hidden product-wide while we rebuild routing + signal quality.
    // Reject Kalshi URLs at the parser so no downstream surface ever sees them.
    if (platform === "kalshi") {
      return {
        type: "url", platform: "kalshi", raw: url.toString(),
        hint: "Decyzon is Polymarket-only at launch.",
        isValidMarket: false, marketSlug: null, marketId: null, reason: "kalshi_unavailable",
      };
    }

    const cleaned = stripTracking(url);
    const segments = cleaned.pathname.split("/").filter(Boolean);
    if (segments.length === 0) {
      return {
        type: "url", platform, raw: cleaned.toString(),
        hint: `Polymarket link detected, but no market path.`,
        isValidMarket: false, marketSlug: null, marketId: null, reason: "no_market_path",
      };
    }

    const slug = segments[segments.length - 1];
    const label = "Polymarket market detected";
    return {
      type: "url", platform, raw: cleaned.toString(), hint: label,
      isValidMarket: true, marketSlug: slug, marketId: slug, reason: "ok_market",
    };
  }

  // Free-text question
  return {
    type: "question", platform: "other", raw: normalized,
    hint: "Question mode — pasting a market URL gives a sharper read.",
    isValidMarket: false, marketSlug: null, marketId: null, reason: "ok_question",
  };
}
